import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, RefreshCw, Search, Filter } from 'lucide-react';
import { api, PlannedVsActualRow, reportsApi } from '../api';

interface PartnerOption {
  mp_id: number;
  name: string;
}

type StageValueType = 'datetime' | 'date' | 'array';

interface StageRowDefinition {
  label: string;
  type: StageValueType;
  planned: string | string[] | null;
  actual: string | string[] | null;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const ReportsPlannedVsActual: React.FC = () => {
  const [partners, setPartners] = useState<PartnerOption[]>([]);
  const [selectedPartner, setSelectedPartner] = useState<string>('');
  const [batchCodeInput, setBatchCodeInput] = useState('');
  const [batchCodeFilter, setBatchCodeFilter] = useState('');
  const [rows, setRows] = useState<PlannedVsActualRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [total, setTotal] = useState(0);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);
  const start = total > 0 ? (page - 1) * pageSize + 1 : 0;
  const end = total > 0 ? Math.min(page * pageSize, total) : 0;
  const currentPage = total > 0 ? Math.min(page, totalPages) : 1;

  useEffect(() => {
    fetchPartners();
  }, []);

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, selectedPartner, batchCodeFilter]);

  const fetchPartners = async () => {
    try {
      const response = await api.get<{ success: boolean; data: PartnerOption[] }>('/partners');
      if (response.data?.success && Array.isArray(response.data.data)) {
        const sorted = [...response.data.data].sort((a, b) => a.name.localeCompare(b.name));
        setPartners(sorted);
      }
    } catch (err) {
      console.error('Failed to load partners for report filters.', err);
    }
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: {
        page: number;
        pageSize: number;
        mpId?: number;
        batchCode?: string;
      } = {
        page,
        pageSize,
      };

      if (selectedPartner) {
        params.mpId = Number(selectedPartner);
      }

      if (batchCodeFilter) {
        params.batchCode = batchCodeFilter;
      }

      const response = await reportsApi.getPlannedVsActual(params);
      const { pagination, data } = response;
      const lastPage = Math.max(1, Math.ceil(pagination.total / pagination.pageSize));

      if (pagination.total > 0 && page > lastPage) {
        setPage(lastPage);
        return;
      }

      setRows(data);
      setTotal(pagination.total);
    } catch (err: any) {
      console.error('Failed to fetch planned vs actual report.', err);
      const message = err?.response?.data?.message ?? 'Failed to fetch report data.';
      setError(message);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBatchCodeFilter(batchCodeInput.trim());
    setPage(1);
  };

  const handleResetFilters = () => {
    setSelectedPartner('');
    setBatchCodeInput('');
    setBatchCodeFilter('');
    setPage(1);
  };

  const handleExportCsv = () => {
    if (!rows.length) return;

    const headers = [
      'Partner',
      'Batch Code',
      'Status',
      'Cancelled',
      'Program',
      'Pre Assessment Planned',
      'Pre Assessment Actual',
      'Post Assessment Planned',
      'Post Assessment Actual',
      'Batch Launch Planned',
      'Batch Launch Actual',
      'Placement Planned',
      'Placement Actual',
      'Basecamp Planned Start',
      'Basecamp Actual Start',
      'Basecamp Planned Dates',
      'Basecamp Actual Dates',
      'Domain Planned Start',
      'Domain Actual Start',
      'Domain Planned Dates',
      'Domain Actual Dates',
      'OJT Planned Start',
      'OJT Actual Start',
      'OJT Planned Dates',
      'OJT Actual Dates',
    ];

    const csvLines = rows.map((row) => [
      row.partner.name ?? '',
      row.batchCode,
      row.status ?? '',
      row.cancelled ? 'Yes' : 'No',
      row.program.name ?? '',
      formatValue(row.preAssessment.planned, 'datetime'),
      formatValue(row.preAssessment.actual, 'datetime'),
      formatValue(row.postAssessment.planned, 'datetime'),
      formatValue(row.postAssessment.actual, 'datetime'),
      formatValue(row.batchLaunch.planned, 'date'),
      formatValue(row.batchLaunch.actual, 'date'),
      formatValue(row.placementDrive.planned, 'date'),
      formatValue(row.placementDrive.actual, 'date'),
      formatValue(row.basecamp.plannedStart, 'date'),
      formatValue(row.basecamp.actualStart, 'date'),
      formatValue(row.basecamp.planned, 'array'),
      formatValue(row.basecamp.actual, 'array'),
      formatValue(row.domainTraining.plannedStart, 'date'),
      formatValue(row.domainTraining.actualStart, 'date'),
      formatValue(row.domainTraining.planned, 'array'),
      formatValue(row.domainTraining.actual, 'array'),
      formatValue(row.ojt.plannedStart, 'date'),
      formatValue(row.ojt.actualStart, 'date'),
      formatValue(row.ojt.planned, 'array'),
      formatValue(row.ojt.actual, 'array'),
    ]);

    const csvContent = [
      headers.map(escapeCsv).join(','),
      ...csvLines.map((line) => line.map((value) => escapeCsv(String(value ?? ''))).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `planned-vs-actual-report-${new Date().toISOString()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleRefresh = () => {
    fetchReport();
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Link
            to="/dashboard/reports"
            className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Reports
          </Link>
          <h1 className="mt-3 text-2xl font-bold text-gray-900">Planned vs Actual Report</h1>
          <p className="mt-1 text-sm text-gray-700">
            Track planned versus actual timelines across batches and export milestones for further analysis.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={handleRefresh}
            disabled={loading}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleExportCsv}
            disabled={!rows.length}
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <form className="grid grid-cols-1 gap-4 sm:grid-cols-4" onSubmit={handleSearchSubmit}>
          <div>
            <label htmlFor="partner-filter" className="form-label">
              Partner
            </label>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <select
                id="partner-filter"
                value={selectedPartner}
                onChange={(event) => {
                  setSelectedPartner(event.target.value);
                  setPage(1);
                }}
                className="form-input pl-10"
              >
                <option value="">All partners</option>
                {partners.map((partner) => (
                  <option key={partner.mp_id} value={partner.mp_id}>
                    {partner.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="page-size" className="form-label">
              Rows per page
            </label>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <select
                id="page-size"
                value={pageSize}
                onChange={handlePageSizeChange}
                className="form-input pl-10"
              >
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-end gap-2">
            <button type="submit" className="btn-secondary w-full">
              Apply
            </button>
            <button
              type="button"
              className="w-full rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
              onClick={handleResetFilters}
              disabled={loading}
            >
              Clear
            </button>
          </div>
        </form>

        {error && (
          <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700 mt-4">
            {error}
          </div>
        )}
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            Planned vs Actual ({total})
          </h2>
        </div>

        {loading ? (
          <div className="px-6 py-12 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : rows.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batch</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Planned</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actual</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 text-sm text-gray-700">
                {rows.map((row) => {
                  const stageData: StageRowDefinition[] = [
                    { label: 'Pre-Assessment', type: 'datetime', planned: row.preAssessment.planned, actual: row.preAssessment.actual },
                    { label: 'Post-Assessment', type: 'datetime', planned: row.postAssessment.planned, actual: row.postAssessment.actual },
                    { label: 'Batch Launch', type: 'date', planned: row.batchLaunch.planned, actual: row.batchLaunch.actual },
                    { label: 'Placement Drive', type: 'date', planned: row.placementDrive.planned, actual: row.placementDrive.actual },
                    { label: 'Basecamp Dates', type: 'array', planned: row.basecamp.planned, actual: row.basecamp.actual },
                    { label: 'Domain Training Dates', type: 'array', planned: row.domainTraining.planned, actual: row.domainTraining.actual },
                    { label: 'OJT Dates', type: 'array', planned: row.ojt.planned, actual: row.ojt.actual },
                  ];

                  return (
                    <React.Fragment key={row.batchId}>
                      {stageData.map((stage, index) => (
                        <tr key={`${row.batchId}-${stage.label}`} className="hover:bg-gray-50">
                          {index === 0 && (
                            <td className="px-6 py-4 align-top" rowSpan={stageData.length}>
                              <div className="flex items-center gap-2">
                                <div className="text-sm font-semibold text-gray-900">{row.batchCode}</div>
                                {row.cancelled && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    Cancelled
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">{row.partner.name ?? '-'}</div>
                              {row.program?.name && (
                                <div className="text-xs text-gray-500 mt-1">{row.program.name}</div>
                              )}
                              {row.mobilisationTitle && (
                                <div className="text-xs text-gray-400 mt-1">{row.mobilisationTitle}</div>
                              )}
                            </td>
                          )}
                          <td className="px-6 py-4 align-top text-sm font-medium text-gray-900">{stage.label}</td>
                          <td className="px-6 py-4 align-top text-sm text-gray-700">{formatValue(stage.planned, stage.type)}</td>
                          <td className="px-6 py-4 align-top text-sm text-gray-700">{formatValue(stage.actual, stage.type)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-6 py-12 text-center text-sm text-gray-500">
            {batchCodeFilter || selectedPartner ? 'No batches match the applied filters.' : 'No batches available yet.'}
          </div>
        )}

        <div className="px-6 py-4 bg-white border-t border-gray-200 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-gray-700">
            {total > 0 ? (
              <>
                Showing <span className="font-medium">{start}</span> to{' '}
                <span className="font-medium">{end}</span> of{' '}
                <span className="font-medium">{total}</span> results
              </>
            ) : (
              'No results'
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={currentPage <= 1}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            >
              Previous
            </button>
            <span className="text-sm text-gray-700">
              Page {currentPage} of {totalPages || 1}
            </span>
            <button
              type="button"
              className="inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={currentPage >= totalPages || total === 0}
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function formatValue(value: string | string[] | null | undefined, type: StageValueType): string {
  if (type === 'array') {
    return formatDateArray(value as string[] | null);
  }

  if (type === 'datetime') {
    return formatDateTime(value as string | null);
  }

  return formatDateOnly(value as string | null);
}

function formatDateArray(values: string[] | null | undefined): string {
  if (!values || values.length === 0) return '-';
  return values.map((item) => formatDateOnly(item)).join(', ');
}

function formatDateOnly(value: string | null | undefined): string {
  if (!value) return '-';
  const date = value.includes('T') ? new Date(value) : new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return value;
  return formatAsDayMonthYear(date);
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const datePart = formatAsDayMonthYear(date);
  const timePart = date.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata',
  });
  return `${datePart}, ${timePart} IST`;
}

function formatAsDayMonthYear(date: Date): string {
  const formatter = new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Asia/Kolkata',
  });
  const parts = formatter.formatToParts(date);
  const day = parts.find((part) => part.type === 'day')?.value ?? '';
  const month = parts.find((part) => part.type === 'month')?.value ?? '';
  const year = parts.find((part) => part.type === 'year')?.value ?? '';
  if (!day || !month || !year) return formatter.format(date);
  return `${day}/${month}/${year}`;
}

function escapeCsv(value: string) {
  const safe = value.replace(/"/g, '""');
  return `"${safe}"`;
}

export default ReportsPlannedVsActual;
